
import { ISessionService } from './sessions/sessionService';
import { UserInfoService } from '../features/userInfo/services/UserInfoService';
import { ICacheService } from './iCacheService';
import { AccountInfo } from '@azure/msal-browser';
import { IMsalAuthService, MsalAuthService } from './msal/msalAuthService';

export interface IStateChangeService {
    initialize(): void;
    deepLinkToState: ng.ui.IState;
    deepLinkToParams: any;
    areDeepLinking: boolean;
}

export class StateChangeService implements IStateChangeService {
    isPopulatingCache: boolean;

    private _deepLinkToState: ng.ui.IState;
    private _deepLinkToParams: any;

    set deepLinkToState(state) {
        this._deepLinkToState = state;
    }

    get deepLinkToState(): ng.ui.IState {
        var savedDeepLinkState = this._deepLinkToState;
        this._deepLinkToState = undefined;
        return savedDeepLinkState;
    }

    set deepLinkToParams(params) {
        this._deepLinkToParams = params;
    }

    get deepLinkToParams(): ng.ui.IState {
        var savedDeepLinkToParams = this._deepLinkToParams;
        this._deepLinkToParams = undefined;
        return savedDeepLinkToParams;
    }

    get areDeepLinking(): boolean {
        return <boolean>this._deepLinkToState;
    }

    static $inject: string[] = [
        '$rootScope', '$state', 'UserInfoService', 'SessionService', 'CacheService', 'LogoutService', '$location', 'MsalAuthService'
    ];

    constructor(private _$rootScope,
        private _$state: ng.ui.IStateService,
        private _userInfoService: UserInfoService,
        private _sessionService: ISessionService,
        private _cacheService: ICacheService,
        private _logoutService: Authentication.LogoutService,
        private $location: ng.ILocationService,
        private _msalAuth: IMsalAuthService
    ) {
        this.isPopulatingCache = false;

        /* This State Change service is fired upon app load and kicks off the login process. 
         * This MSAL PublicClientApplication code HAS to be called from this spot to handle the redirect from logging in.
         * The MSAL Browser library is implementing Auth Code Flow
         */

        _msalAuth.msal()
            .handleRedirectPromise()
            .then((tokenResponse) => {
                // Handle redirect response
                let accountObj: AccountInfo = null;
                if (tokenResponse !== null) {
                    accountObj = tokenResponse.account;
                } else {
                    const currentAccounts = _msalAuth.msal().getAllAccounts();
                    if (!currentAccounts || currentAccounts.length === 0) {
                        // No user signed in
                        _msalAuth.msal().loginRedirect();
                        return;
                    } else if (currentAccounts.length > 1) {
                        // More than one user signed in, find desired user with getAccountByUsername(username)
                    } else {
                        accountObj = currentAccounts[0];
                    }
                }

                const username = accountObj.username;

                console.log('Logged in user : ', username);

                _msalAuth.msal().setActiveAccount(accountObj);

                this._userInfoService.loadUserInfo().then(() => {
                    _$state.go('requests');
                })
                
            })
            .catch((error) => {
                // Handle redirect error
                console.error('handleRedirectPromise', error);

            });
    }

    initialize(): void {
        this._$rootScope.$on('$stateChangeStart', this.handleStateChangeStart.bind(this));
        this._$rootScope.$on('$stateChangeSuccess', this.handleStateChangeSuccess.bind(this));
    }

    private async handleStateChangeStart(event, toState, toParams, fromState, fromParams): Promise<void> {
        if (toState.name === 'unauthorized')
            return;

        if (toState.url === '/') {
            if (fromState.name === '') {
                this._$rootScope.isBootstrapping = true;
            } else {
                event.preventDefault();
                return;
            }
        }

        if (!this._sessionService.isActive) {
            sessionStorage.setItem('adal.requested.state', toState.name);
            sessionStorage.setItem('adal.requested.stateParams', JSON.stringify(toParams));
            
            event.preventDefault();
            return;
        }

        this.populateCache(event, toState, toParams, fromState, fromParams);       
    }

    private handleStateChangeSuccess() {
        if (!this.isPopulatingCache) {
            this._$rootScope.isBootstrapping = false;
        }
    }

    private populateCache(event, toState, toParams, fromState, fromParams): void {
        if (this._cacheService.areCachesPopulated() || this._userInfoService.isUserInfoPopulated()) {
            //this.ensureAuthorized(event, toState, toParams);
            this.ensureRequestIsValid(event, toState, toParams);
        } else {
            if (!this.isPopulatingCache) {
                const isDefaultState = (toState.url === '/');
                if (!isDefaultState) {
                    event.preventDefault();
                    this._$rootScope.isBootstrapping = false;
                }

                this._$rootScope.isBootstrapping = true;
                this.isPopulatingCache = true;

                this._cacheService.populateCaches().then(() => {
                    this.isPopulatingCache = false;
                    //this.ensureAuthorized(event, toState, toParams);
                    this.ensureRequestIsValid(event, toState, toParams);

                    let stateToNavigateTo = toState.name;
                    let stateParamsToNavigateWith = toParams;
                    if (isDefaultState) {
                        stateToNavigateTo = sessionStorage.getItem('adal.requested.state');
                        stateParamsToNavigateWith = JSON.parse(sessionStorage.getItem('adal.requested.stateParams'));

                        if (stateToNavigateTo === null || stateToNavigateTo === 'splash' || stateToNavigateTo === 'unexpected')
                            stateToNavigateTo = 'requests';
                    }

                    this._$rootScope.isBootstrapping = false;
                    this._$state.go(stateToNavigateTo, stateParamsToNavigateWith);
                }, (response) => {
                    this._$rootScope.isBootstrapping = false;
                    if (response === 'unauthorized') {
                        this._$state.go('unauthorized', { message: 'inaccessible' });
                    } else {
                        this._$state.go('unexpected', { link: 'logout' });
                    }
                });
            }
        }
    }

    private isAuthorized(toState): boolean {
        //console.log('toState');
        //console.log(toState);
        const activity = this.getStateProperty(toState, p => p.requiredActivity);
        //console.log('activity');
        //console.log(activity);
        return (!activity);
    }

    private getStateProperty(state: any, propertyToFetch: Function): string {
        var stateToAuthorize = state,
            requiredFeature: string;
        while (!requiredFeature && stateToAuthorize && stateToAuthorize.data) {
            requiredFeature = propertyToFetch(stateToAuthorize.data);
            if (requiredFeature || !stateToAuthorize.data.navParent)
                break;
            stateToAuthorize = this._$state.get(stateToAuthorize.data.navParent);
        }

        return requiredFeature;
    }

    private ensureRequestIsValid(event, toState, toParams) {

        //console.log('ensureRequestIsValid');
        //console.log(event);
        //console.log(toState);
        //console.log(toParams);

    }
}


/** WEBPACK FOOTER **
 ** ./DeliveryCreate/UI/app/core/stateChangeService.ts
 **/